import { createRouter, createWebHistory } from "vue-router";
import { pinia, useAuthStore } from "@/stores";
const routes = [
    {
        path: "/gorev-atama",
        name: "gorev-atama",
        component: () => import("../views/GorevAtamaView.vue"),
        meta: {
            requiresAuth: true,
            permission: "fleet",
            chatPackage: "taskassign",
        },
    },
    {
        path: "/login",
        name: "login",
        component: () => import("../views/LoginView.vue"),
        meta: { requiresGuest: true },
    },
    {
        path: "/",
        name: "home",
        component: () => import(/* webpackChunkName: "home" */ "../views/HomeView.vue"),
        meta: { requiresAuth: true },
    },
    {
        path: "/frekans-optimizasyon",
        name: "frekans-optimizasyon",
        component: () => import("../views/FrequencyOptimizationView.vue"),
        meta: {
            requiresAuth: true,
            permission: "frequency",
            chatPackage: "freqopt",
        },
    },
    {
        path: "/toplu-frekans-optimizasyon",
        redirect: "/frekans-optimizasyon",
    },
    {
        path: "/senaryo-yonetimi",
        name: "senaryo-yonetimi",
        component: () => import("../views/ScenarioManagerView.vue"),
        meta: {
            requiresAuth: true,
            permission: "frequency",
            chatPackage: "scenario",
        },
    },
    {
        path: "/senaryo-karsilastirma",
        name: "senaryo-karsilastirma",
        component: () => import("../views/ScenarioCompareView.vue"),
        meta: {
            requiresAuth: true,
            permission: "frequency",
            chatPackage: "scenario",
        },
    },
    {
        path: "/arac-sofor-optimizasyon",
        name: "arac-sofor-optimizasyon",
        component: () => import("../views/FleetOptimizationView.vue"),
        meta: {
            requiresAuth: true,
            permission: "fleet",
            chatPackage: "fleetopt",
        },
    },
    {
        path: "/rota-optimizasyon",
        name: "rota-optimizasyon",
        component: () => import("../views/RouteOptimizationView.vue"),
        meta: {
            requiresAuth: true,
            permission: "route",
            chatPackage: "routeopt",
        },
    },
    {
        path: "/rekabetcilik-analizi",
        name: "rekabetcilik-analizi",
        component: () => import("../views/CompetitionAnalysisView.vue"),
        meta: {
            requiresAuth: true,
            permission: "competition",
            chatPackage: "competition",
        },
    },
    {
        path: "/rekabetcilik-analizi/hat-cifti/:hat1/:hat2",
        name: "rekabetcilik-hat-cifti",
        redirect: (to) => ({
            name: "rekabetcilik-analizi",
            query: {
                hat1: decodeURIComponent(String(to.params.hat1 || "")),
                hat2: decodeURIComponent(String(to.params.hat2 || "")),
                ...(to.query.threshold ? { threshold: to.query.threshold } : {}),
            },
        }),
    },
    {
        path: "/gtfs-yonetimi",
        name: "gtfs-yonetimi",
        component: () => import(/* webpackChunkName: "gtfs-editor" */ "../views/GTFSView.vue"),
        meta: {
            requiresAuth: true,
            permission: "gtfs",
            chatPackage: "gtfs",
        },
    },
    {
        path: "/storyboard",
        name: "storyboard",
        component: () => import("../views/StoryBoard.vue"),
        meta: {
            requiresAuth: true,
            chatPackage: "storyboard",
        },
    },
    {
        path: "/storyboard/present/:id",
        name: "storyboard-present",
        component: () => import("../views/StoryboardPresentationView.vue"),
        meta: {
            requiresAuth: true,
            hideChat: true,
        },
    },
    {
        path: "/storyboard/view/:id",
        name: "storyboard-view",
        component: () => import("../views/StoryboardReadonlyView.vue"),
        meta: {
            requiresAuth: true,
            hideChat: true,
        },
    },
    {
        path: "/gtfs-yonetimi/v2",
        redirect: "/gtfs-yonetimi",
    },
    {
        path: "/is-zekasi-raporlari",
        name: "is-zekasi-raporlari",
        component: () => import("../views/BusinessIntelligenceView.vue"),
        meta: { requiresAuth: true, permission: "bi" },
    },
    {
        path: "/admin-panel",
        name: "admin-panel",
        component: () => import("../views/AdminPanelView.vue"),
        meta: { requiresAuth: true, minRole: "admin" },
    },
    {
        path: "/monitoring",
        name: "monitoring",
        component: () => import("../views/MonitoringView.vue"),
        meta: { requiresAuth: true, minRole: "superadmin" },
    },
    {
        path: "/forbidden",
        name: "forbidden",
        component: () => import("../views/ForbiddenView.vue"),
    },
    {
        path: "/about",
        name: "about",
        component: () => import(/* webpackChunkName: "about" */ "../views/AboutView.vue"),
        meta: { requiresAuth: true },
    },
    {
        path: "/logout",
        name: "logout",
        beforeEnter: async (_to, _from, next) => {
            const auth = useAuthStore(pinia);
            await auth.logout();
            next("/login");
        },
    },
    {
        path: "/:pathMatch(.*)*",
        name: "not-found",
        redirect: "/",
    },
];
const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});
const ROLE_HIERARCHY = {
    superadmin: 3,
    admin: 2,
    user: 1,
};
function hasMinRole(userRole, minRole) {
    const hierarchy = ROLE_HIERARCHY;
    return (hierarchy[userRole] || 0) >= (hierarchy[minRole] || 0);
}
const MODULE_PERMISSION_TO_LICENSE = {
    fleet: "fleet_optimization",
    frequency: "frequency_optimization",
    gtfs: "gtfs",
    competition: "competition_analysis",
    route: "route_optimization",
    task: "task_assignment",
    bi: "business_intelligence",
};
router.beforeEach(async (to, _from, next) => {
    const auth = useAuthStore(pinia);
    await auth.ensureSession();
    const isAuthenticated = auth.isLoggedIn;
    if (!isAuthenticated) {
        if (to.name === "login") {
            next();
            return;
        }
        next({
            path: "/login",
            query: to.fullPath !== "/" ? { redirect: to.fullPath } : {},
        });
        return;
    }
    if (to.meta.requiresGuest) {
        next("/");
        return;
    }
    const userRole = auth.userRole;
    const isAdminOnlyRoute = to.meta.minRole === "admin" || to.meta.adminOnly;
    if (!isAdminOnlyRoute && userRole !== "superadmin") {
        let tenantActive = auth.tenantLicense?.active;
        if (tenantActive === undefined && !auth.licenseLoaded) {
            await auth.loadLicenseData();
            tenantActive = auth.tenantLicense?.active;
        }
        if (tenantActive === false) {
            next({ name: "forbidden", query: { reason: "license_expired" } });
            return;
        }
    }
    if (to.meta.minRole && !hasMinRole(userRole, to.meta.minRole)) {
        next("/forbidden");
        return;
    }
    if (to.meta.adminOnly && !hasMinRole(userRole, "admin")) {
        next("/forbidden");
        return;
    }
    const requiredPermission = to.meta.permission;
    if (requiredPermission) {
        if (!auth.hasModuleAccess(requiredPermission)) {
            next("/forbidden");
            return;
        }
        if (userRole !== "superadmin" && userRole === "user") {
            if (!auth.licenseLoaded) {
                await auth.loadLicenseData();
            }
            const moduleName = MODULE_PERMISSION_TO_LICENSE[requiredPermission] ||
                requiredPermission;
            const mod = auth.licensedModules.find((m) => m.modul_adi === moduleName);
            if (mod && !mod.aktif_mi) {
                next({ name: "forbidden", query: { reason: "module_not_licensed" } });
                return;
            }
        }
    }
    try {
        const authData = localStorage.getItem("transportation_auth");
        if (authData) {
            const sessionData = JSON.parse(authData);
            sessionData.lastActivity = new Date().toISOString();
            localStorage.setItem("transportation_auth", JSON.stringify(sessionData));
        }
    }
    catch (error) {
        console.warn("Session activity update failed:", error);
    }
    next();
});
export default router;
